import { cn } from '@mds/shared/lib/utils';

import Field, { selectCls } from '@/components/ui/field';
import Select from '@mds/shared/ui/select';
import type { DeskPackageRateCardItem } from '@mds/shared/types/rate-card';

import { packageSummary } from '@/domain/packages';
import { formatCents } from '@/lib/money';

/** Cards read better on a roomy form; a dropdown keeps a dense one short. */
type Variant = 'cards' | 'select';

/**
 * Which desk package the customer is buying.
 *
 * Takes rate-card items as the API serves them, amounts in cents, so the front
 * desk can never quote a number the Packages & pricing page disagrees with.
 */
export default function PackagePicker({
  packages,
  value,
  onChange,
  variant = 'cards',
  error,
  onBlur,
}: {
  /** Only packages on sale — an off-sale one is refused for a new booking. */
  packages: DeskPackageRateCardItem[];
  value: string;
  onChange: (id: string) => void;
  variant?: Variant;
  /** The select variant only: spread from the page's `validation.field`. */
  error?: string;
  onBlur?: () => void;
}) {
  if (variant === 'select') {
    const selected = packages.find((p) => p.id === value);
    return (
      // The note only describes the package in hand, so it follows the choice
      // rather than crowding every option.
      <Field
        label="Desk package"
        required
        error={error}
        onBlur={onBlur}
        hint={
          selected
            ? [selected.note, packageSummary(selected.package)].filter(Boolean).join(' · ')
            : undefined
        }
      >
        <Select
          variant="field"
          className={selectCls}
          value={value}
          onChange={onChange}
          options={packages.map((p) => ({
            value: p.id,
            label: `${p.name} · ${formatCents(p.amountMinor)}/${p.unit}`,
          }))}
          aria-label="Desk package"
        />
      </Field>
    );
  }

  return (
    <fieldset className="m-0 border-0 p-0">
      <legend className="mb-2 font-mono text-sm font-semibold tracking-wider text-ink-secondary uppercase">
        Desk package
      </legend>
      <ul className="m-0 flex list-none flex-col gap-2 p-0">
        {packages.map((p) => {
          const selected = p.id === value;
          return (
            <li key={p.id}>
              <button
                type="button"
                onClick={() => onChange(p.id)}
                aria-pressed={selected}
                className={cn(
                  'flex min-h-14 w-full items-center justify-between gap-3 rounded-xl border px-4 py-2.5 text-left transition-colors focus-visible:outline-(length:--focus-ring-width) focus-visible:outline-offset-(--focus-ring-offset) focus-visible:outline-(color:--focus-ring-color)',
                  selected
                    ? 'border-brand-edge bg-brand-wash'
                    : 'border-hairline bg-surface hover:border-brand-edge',
                )}
              >
                <span className="flex min-w-0 flex-col gap-0.5">
                  <span className="truncate text-base font-semibold">{p.name}</span>
                  <span className="truncate text-md text-ink-muted">
                    {[p.note, packageSummary(p.package)].filter(Boolean).join(' · ')}
                  </span>
                </span>
                <span className="shrink-0 text-right">
                  <span className="block text-lg font-semibold tabular-nums">
                    {formatCents(p.amountMinor)}
                  </span>
                  <span className="block font-mono text-sm text-ink-muted">/{p.unit}</span>
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </fieldset>
  );
}
